import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, Trash2, Clock, Download, X } from 'lucide-react'
import Card from './Card'
import Button from './Button'
import { getAllDrafts, deleteDraftById, bulkDeleteDrafts } from '../services/draftManagement'
import { formatDate } from '../utils/helpers'
import { useToast } from '../contexts/ToastContext'

const DraftManager = ({ organizationId, userId, formType = 'examination', onLoadDraft, onClose }) => {
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [selectedDraftIds, setSelectedDraftIds] = useState([])
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const { success, showError } = useToast()

  useEffect(() => {
    loadDrafts()
  }, [organizationId, userId])

  const loadDrafts = async () => {
    if (!organizationId || !userId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const allDrafts = await getAllDrafts(organizationId, userId, formType)
      setDrafts(allDrafts)
      // Clear selection when reloading
      setSelectedDraftIds([])
    } catch (error) {
      console.error('Error loading drafts:', error)
      showError('Failed to load drafts')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (draftId) => {
    if (!confirm('Are you sure you want to delete this draft? This action cannot be undone.')) {
      return
    }

    try {
      setDeleting(draftId)
      await deleteDraftById(draftId, organizationId, userId, formType)
      success('Draft deleted successfully')
      loadDrafts()
    } catch (error) {
      console.error('Error deleting draft:', error)
      showError('Failed to delete draft')
    } finally {
      setDeleting(null)
    }
  }

  const handleLoad = (draft) => {
    if (onLoadDraft) {
      onLoadDraft(draft)
    }
    if (onClose) {
      onClose()
    }
  }

  const toggleSelect = (draftId) => {
    setSelectedDraftIds((prev) =>
      prev.includes(draftId) ? prev.filter((id) => id !== draftId) : [...prev, draftId]
    )
  }

  const isSelected = (draftId) => selectedDraftIds.includes(draftId)

  const handleSelectAll = () => {
    if (selectedDraftIds.length === drafts.length) {
      setSelectedDraftIds([])
    } else {
      setSelectedDraftIds(drafts.map((d) => d.id))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedDraftIds.length === 0) return

    if (
      !confirm(
        `Delete ${selectedDraftIds.length} selected draft${
          selectedDraftIds.length > 1 ? 's' : ''
        }? This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      setBulkDeleting(true)
      // Use optimized bulk delete (batches server deletes)
      const results = await bulkDeleteDrafts(selectedDraftIds, organizationId, userId, formType)
      
      if (results.errors.length > 0) {
        showError(`Deleted ${results.localDeleted + results.serverDeleted} drafts, but ${results.errors.length} failed`)
      } else {
        success(`Successfully deleted ${results.localDeleted + results.serverDeleted} draft${results.localDeleted + results.serverDeleted !== 1 ? 's' : ''}`)
      }
      
      await loadDrafts()
    } catch (error) {
      console.error('Error deleting selected drafts:', error)
      showError('Failed to delete selected drafts')
    } finally {
      setBulkDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-500">Loading drafts...</p>
      </div>
    )
  }

  if (drafts.length === 0) {
    return (
      <div className="p-6 text-center">
        <FileText size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 dark:text-gray-400 mb-2">No saved drafts found</p>
        <p className="text-sm text-gray-500 dark:text-gray-500">Your {formType === 'session' ? 'session' : 'examination'} data will be automatically saved as you work.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {/* Bulk actions toolbar */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span>
            {selectedDraftIds.length > 0
              ? `${selectedDraftIds.length} selected`
              : `${drafts.length} draft${drafts.length !== 1 ? 's' : ''}`}
          </span>
          {drafts.length > 1 && (
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {selectedDraftIds.length === drafts.length ? 'Clear selection' : 'Select all'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleBulkDelete}
            disabled={selectedDraftIds.length === 0 || bulkDeleting}
            className="flex items-center gap-1 text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            {bulkDeleting ? (
              <Clock size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
            <span className="text-xs">Delete selected</span>
          </Button>
        </div>
      </div>

      {drafts.map((draft) => (
        <Card key={draft.id} variant="soft" className="p-3">
          <div className="flex items-start justify-between gap-3">
            {/* Selection checkbox */}
            <div className="pt-1">
              <input
                type="checkbox"
                checked={isSelected(draft.id)}
                onChange={() => toggleSelect(draft.id)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <FileText size={18} className="text-primary-600 flex-shrink-0" />
                <span className="font-semibold text-gray-900 dark:text-white truncate">
                  {draft.formType === 'session' ? 'Session Draft' : 'Examination Draft'}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  draft.source === 'local' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {draft.source === 'local' ? 'Local' : 'Server'}
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-xs text-gray-600 mb-2">
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>{formatDate(draft.savedAt)}</span>
                </div>
                {draft.currentStep !== undefined && (
                  <span>Step {draft.currentStep + 1}</span>
                )}
              </div>

              {draft.formData && (
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  {draft.formData.firstName && draft.formData.lastName && (
                    <p>
                      <strong>Client:</strong> {draft.formData.firstName} {draft.formData.lastName}
                    </p>
                  )}
                  {draft.customQuestions && draft.customQuestions.length > 0 && (
                    <p>
                      <strong>Custom Questions:</strong> {draft.customQuestions.length}
                    </p>
                  )}
                </div>
              )}
              {draft.formType === 'session' && (
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  {draft.sessionDuration !== undefined && (
                    <p>
                      <strong>Duration:</strong> {Math.floor((draft.sessionDuration || 0) / 60)} min
                    </p>
                  )}
                  {draft.vitalSigns && draft.vitalSigns.length > 0 && (
                    <p>
                      <strong>Vital Signs:</strong> {draft.vitalSigns.length} recorded
                    </p>
                  )}
                  {draft.quickNotes && draft.quickNotes.length > 0 && (
                    <p>
                      <strong>Quick Notes:</strong> {draft.quickNotes.length}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleLoad(draft)}
                className="whitespace-nowrap"
              >
                Continue
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleDelete(draft.id)}
                disabled={deleting === draft.id}
                className="text-red-600 hover:text-red-700"
              >
                {deleting === draft.id ? (
                  <Clock size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

export default DraftManager

